<template>
  <div
    class="overflow-y-auto"
    :style="{ height: 'calc(100vh - 4.5rem - 8rem - 3.5rem)' }"
  >
    <div v-if="fetchLoading" class="h-full grid items-center justify-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else>
      <div
        class="sticky z-10 transition-all duration-300"
        :class="[currentForm ? '-top-12' : '-top-14']"
      >
        <img
          class="absolute z-0 h-25 w-auto flex-shrink-0"
          :src="`/static/bg-prospect-generate-link.png`"
        />
        <div class="h-25 py-2 px-6 flex flex-col justify-between">
          <div class="flex items-center justify-between">
            <BlTextSubtitle2
              text="Send form to parents"
              class="text-pure-white"
            />
            <div class="cursor-pointer" @click="learnMoreOpened = true">
              <BlTextBody2
                text="Learn more"
                class="text-pure-white underline"
              />
            </div>
          </div>
          <BlInputButton
            v-if="!currentForm.id"
            :type="prospectStage.is_admitted ? 'primary' : 'outlined'"
            dense
            label="Generate link"
            :loading="generateLoading"
            :disabled="prospectStage.is_admitted"
            :class="{ 'pointer-events-none': prospectStage.is_admitted }"
            @clicked="onGenerateLink"
          />
          <div
            v-else
            class="py-1 px-3 flex items-center justify-between gap-2 rounded"
            :style="{ background: 'rgba(255, 255, 255, 0.8)', zIndex: 1 }"
          >
            <div v-if="generateLoading" class="w-full flex justify-center">
              <div class="blum-loader-xs"></div>
            </div>
            <div
              v-else-if="currentForm.submitted_at"
              class="h-6 px-2 flex items-center gap-2 rounded-full bg-dark-purple-100"
            >
              <BlTextBody3
                :text="
                  `Filled on ${displayDate(
                    currentForm.submitted_at,
                    'DD/MM/YYYY [at] HH:mm'
                  )}`
                "
                class="text-pure-white"
              />
              <BlIcon icon="check-circle" color="white" class="h-4 w-4" />
            </div>
            <div
              v-else
              class="flex items-center gap-2 truncate cursor-pointer"
              :title="`${origin}/prospect-form/${currentForm.slug}`"
              @click="
                copyToClipboard(
                  `${origin}/prospect-form/${currentForm.slug}`,
                  'Prospect Form'
                )
              "
            >
              <BlIcon icon="copy" class="h-5 w-5" />
              <BlTextBody2
                class="text-light-purple-400 truncate"
                :text="`${origin}/prospect-form/${currentForm.slug}`"
              />
            </div>
            <BlInputButton
              type="outlined"
              dense
              label="Generate new link"
              :loading="generateLoading"
              :disabled="prospectStage.is_admitted"
              :class="{ 'pointer-events-none': prospectStage.is_admitted }"
              @clicked="onGenerateLink"
            />
          </div>
        </div>
      </div>
      <div v-if="prospectInformation" class="py-5 px-6 flex flex-col gap-3">
        <ChildSection />
        <FatherSection />
        <MotherSection />
        <ApproximateAgeSection />
        <PregnancyAndBirthSection />
        <ConsultationRecordsSection />
        <AdditionalInformationSection @after-commit="$emit('after-commit')" />
        <OtherGuardianSection />
      </div>
    </div>

    <div
      id="learn-more-information-panel"
      class="fixed w-screen right-0 flex justify-end transition-all duration-500"
      :style="{
        top: learnMoreOpened ? 'calc(4.5rem + 5rem + 3rem)' : 'calc(100vh)',
        height: 'calc(100vh - 4.5rem - 5rem - 3rem)',
        zIndex: 53
      }"
    >
      <div
        class="h-full w-screen absolute left-0 top-0 -z-10"
        @click="learnMoreOpened = false"
      ></div>
      <div class="h-full w-120 flex-shrink-0 flex flex-col relative z-0">
        <div
          class="relative h-full flex flex-col bg-pure-white overflow-y-auto"
        >
          <div class="sticky top-0 z-10 h-12 flex-shrink-0 overflow-hidden">
            <img
              class="absolute z-0 h-25 w-auto flex-shrink-0"
              :src="`/static/bg-prospect-generate-link.png`"
            />
            <div class="h-12 px-6 flex items-center justify-between">
              <div>
                <BlTextSubtitle2
                  text="What is Send Form to Parents?"
                  class="text-pure-white"
                />
              </div>
              <BlInputButton
                v-if="learnMoreOpened"
                type="outlined"
                dense
                icon="close"
                @clicked="learnMoreOpened = false"
              />
            </div>
          </div>
          <div class="py-5 px-6 flex flex-col gap-3">
            <div class="p-4 flex flex-col gap-3 rounded-2xl bg-slate-200">
              <div>
                <BlTextBody2
                  text="Use this feature to send a form to parents, enabling them to fill out additional information about their child. Once they complete the form, the information will be automatically added to the client information section."
                  class="text-slate-800"
                />
              </div>
              <div>
                <BlTextSubtitle3 text="Attention: " class="text-rose-700" />
                <BlTextBody2
                  text="Once parents have filled the form out, the link will become unavailable. Ensure the form is accurate and complete before distribution. Please note that the link will "
                  class="text-slate-800"
                />
                <BlTextBody2
                  text="expire 7 days after it is generated, "
                  class="text-slate-800 font-bold"
                />
                <BlTextBody2
                  text="but you can generate a new link if needed. Admin can always update the information later if necessary."
                  class="text-slate-800"
                />
              </div>
              <div>
                <BlTextBody2
                  text="To modify the form, please visit the Center Settings."
                  class="text-slate-800"
                />
              </div>
            </div>
            <router-link
              to="/center/client-acquisition/form"
              target="_blank"
              class="w-full"
            >
              <BlInputButton
                label="Go to center settings"
                type="outlined"
                class="w-full"
              />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import ChildSection from "./clientInformation/ChildSection.vue";
import FatherSection from "./clientInformation/FatherSection.vue";
import MotherSection from "./clientInformation/MotherSection.vue";
import ApproximateAgeSection from "./clientInformation/ApproximateAgeSection.vue";
import PregnancyAndBirthSection from "./clientInformation/PregnancyAndBirthSection.vue";
import ConsultationRecordsSection from "./clientInformation/ConsultationRecordsSection.vue";
import AdditionalInformationSection from "./clientInformation/AdditionalInformationSection.vue";
import OtherGuardianSection from "./clientInformation/OtherGuardianSection.vue";

import BlTextSubtitle2 from "../../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlTextBody3 from "../../../BlTextBody3.vue";
import BlIcon from "../../../BlIcon.vue";
import BlInputButton from "../../../BlInputButton.vue";

export default {
  name: "ClientInformationTab",
  components: {
    ChildSection,
    FatherSection,
    MotherSection,
    ApproximateAgeSection,
    PregnancyAndBirthSection,
    ConsultationRecordsSection,
    AdditionalInformationSection,
    OtherGuardianSection,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody3,
    BlIcon,
    BlInputButton
  },
  props: {
    // @after-commit
  },
  data() {
    return {
      origin: "",
      fetchLoading: false,
      generateLoading: false,
      learnMoreOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "prospectStage", "prospectInformation"]),
    currentForm() {
      if (!this.prospectInformation) return {};
      return this.prospectInformation.current_form || {};
    }
  },
  mounted() {
    this.fetchLoading = true;
    this.fetchProspect();
    this.origin = window.location.origin;
  },
  methods: {
    ...mapActions(["getProspect", "generateProspectFormLink", "showToast"]),
    displayDate(date, format) {
      if (!date) return "-";
      return moment(date).format(format);
    },
    fetchProspect() {
      const data = { id: this.prospectStage.prospect.id };
      this.getProspect(data).then(({ success }) => {
        this.fetchLoading = false;
        // if (!success) return;
      });
    },
    onGenerateLink() {
      this.generateLoading = true;
      const data = {
        prospect_id: this.prospectInformation.id
      };
      this.generateProspectFormLink(data).then(({ success }) => {
        this.generateLoading = false;
        // if (!success) return;
      });
    },
    copyToClipboard(text, label = "Text") {
      navigator.clipboard.writeText(text);
      const title = `${label} has been copied to clipboard!`;
      this.showToast({ title, message: "", type: "success" });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/modal/prospectPanel/ClientInformationTab.vue